import { Dispatch, SetStateAction, useMemo, useState, useEffect } from "react"
import { Autocomplete, Box, Button, CircularProgress, Dialog, TextField } from "@mui/material"
import AddIcon from '@mui/icons-material/Add'
import { Autoclave, AutoclaveType, OpenAssignBoxModal, SporetestingResult } from "../types"
import CloseModalButton from "../../../../components/CloseModalButton"
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../../components/Dialog.styled"
import { assignBiobox, unassignBiobox, updateAutoclave, updateSporeTest } from "../../../../services/biologicalServices"
import { useAppDispatch } from "../../../../redux/store"
import { addError, addSuccess } from "../../../../redux/alerts"
import { getAutoclaveTypeName, updateSterilizationMethod } from "../utils/helpers"
import { AutoclaveTypeId } from "../enums"

type AssignBoxModal = {
  autoclaves: Autoclave[],
  autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>,
  handleAddAutoclave: () => void,
  sporetests: SporetestingResult[],
  customerNumber: string,
  openModal: OpenAssignBoxModal,
  reload: () => Promise<void>,
  setOpenModal: Dispatch<SetStateAction<OpenAssignBoxModal>>,
}

const AssignBoxModal = (props: AssignBoxModal) => {
  const {
    autoclaves,
    autoclaveTypeById,
    handleAddAutoclave,
    sporetests,
    customerNumber,
    openModal,
    reload,
    setOpenModal,
  } = props

  const { autoclave, biobox } = openModal
  const dispatch = useAppDispatch();
  const [isUpdating, setIsUpdating] = useState(false)
  const [selectedAutoclave, setSelectedAutoclave] = useState<Autoclave | null>(null)

  useEffect(() => {
    if (autoclave) {
      setSelectedAutoclave(autoclave)
    }
  }, [autoclave])

  const boxTests = useMemo(() => {
    const tests = biobox
      ? sporetests.filter(test => Number(test.box_id) === Number(biobox.box_id))
      : []

    return tests
  }, [biobox, sporetests])

  const unassignedTests = useMemo(() => {
    return boxTests.filter(test => test.autoclave_id === 0 && test.test_result !== null)
  }, [boxTests])

  const handleClose = () => setOpenModal({ open: false, autoclave: null, biobox: null })

  const autoclaveTypeName = getAutoclaveTypeName(autoclave, autoclaveTypeById)
  const nextAutoclaveTypeName = getAutoclaveTypeName(selectedAutoclave, autoclaveTypeById)

  const hasChangedAutoclaveType = autoclave && selectedAutoclave
    && Number(autoclave.autoclavetype_id) !== Number(selectedAutoclave?.autoclavetype_id)

  const handleSelectAutoclave = (value: Autoclave | null) => {
    setSelectedAutoclave(value)
  }

  const handleUnassignBiobox = async () => {
    try {
      if (selectedAutoclave && biobox) {
        setIsUpdating(true)

        // Unassign Box from Autoclave
        await unassignBiobox(customerNumber, biobox.box_id)

        // Update Autoclave compliance
        const { active, autoclave_id, autoclave_name, autoclavetype_id, autoclave_brand, autoclave_model, autoclave_serial, autoclave_frequency } = selectedAutoclave
        const autoclavePayload = {
          customer_number: customerNumber,
          autoclave_id,
          autoclave_name,
          autoclavetype_id,
          autoclave_brand,
          autoclave_model,
          autoclave_serial,
          autoclave_frequency,
          active,
        }

        await updateAutoclave(customerNumber, autoclavePayload)

        reload()

        setIsUpdating(false)

        handleClose()
        dispatch(addSuccess('Spore test box unassigned'))
      }
    } catch (err) {
      console.error(err)
      setIsUpdating(false)
      dispatch(addError(err instanceof Error ? err.message : 'There was a problem unassigning the spore test box'))
      handleClose()
    }
  }

  const handleSave = async () => {
    try {
      if (selectedAutoclave && biobox) {
        setIsUpdating(true)

        // Assign Box to Autoclave
        const boxId = biobox.box_id
        const autoclaveId = selectedAutoclave.autoclave_id

        await assignBiobox(customerNumber, autoclaveId, boxId)

        // Update Autoclave compliance
        const { active, autoclave_id, autoclave_name, autoclavetype_id, autoclave_brand, autoclave_model, autoclave_serial, autoclave_frequency } = selectedAutoclave
        const autoclavePayload = {
          customer_number: customerNumber,
          autoclave_id,
          autoclave_name,
          autoclavetype_id,
          autoclave_brand,
          autoclave_model,
          autoclave_serial,
          autoclave_frequency,
          active
        }

        await updateAutoclave(customerNumber, autoclavePayload)

        // Assign Box Sporetests to Autoclave
        const updatedSporeTests = unassignedTests.map(testResult => {
          const clonedTest = structuredClone(testResult)
          clonedTest.autoclave_id = selectedAutoclave.autoclave_id
          return updateSporeTest(customerNumber, clonedTest)
        })

        await Promise.all(updatedSporeTests)

        // Update Box or Tests with Sterilization Method
        if (selectedAutoclave.autoclavetype_id) {
          await updateSterilizationMethod(customerNumber, selectedAutoclave.autoclavetype_id, biobox.box_number, boxTests)
        }

        reload()

        setIsUpdating(false)

        handleClose()
        dispatch(addSuccess('Spore test box updated'))
      }
    } catch (err) {
      console.error(err)
      setIsUpdating(false)
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      dispatch(addError(error?.response?.data?.message || error?.message || 'There was a problem updating the spore test box'))
      handleClose()
    }
  }

  const disableUnassignBiobox = isUpdating
    || !(selectedAutoclave && biobox && Number(selectedAutoclave.autoclave_id) === Number(biobox.autoclave_id))

  const disableSave = isUpdating
    || !(selectedAutoclave && biobox && Number(selectedAutoclave.autoclave_id) !== Number(biobox.autoclave_id))

  return (
    <>
      <Dialog
        open={openModal.open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth={true}
      >
        <CloseModalButton handleClose={handleClose} />
        <StyledDialogTitle>
          <Box className="flex flex-align-center c-g-8">
            <AddIcon fontSize="small" />
            <span>Assign Spore Test Box</span>
          </Box>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Box className="flex flex-center">
            <Autocomplete
              disabled={isUpdating}
              value={selectedAutoclave}
              onChange={(event, value) => handleSelectAutoclave(value)}
              options={autoclaves}
              getOptionLabel={option => String(option.autoclave_name)}
              isOptionEqualToValue={(option: Autoclave, value: Autoclave) => {
                return option.autoclave_id === value.autoclave_id
              }}
              size="small"
              style={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select an autoclave"
                />
              )}
            />
          </Box>
          {
            hasChangedAutoclaveType && (
              <Box sx={{ pl: '12px', pr: '12px', pt: '4px' }} className="color-danger">
                Attention: You are trying to assign a box that is currently designated for {autoclaveTypeName} to a {nextAutoclaveTypeName} autoclave. Press OK if this is correct.
              </Box>
            )}
          <Box className="flex flex-end m-t-16" columnGap="16px">
            {
              Boolean(biobox?.autoclave_id) && (
                <Button size="small" variant="contained" onClick={handleUnassignBiobox} disabled={disableUnassignBiobox}>
                  Unassign Box
                </Button>
              )
            }
            <Button size="small" variant="contained" onClick={handleAddAutoclave} disabled={isUpdating}>
              Add Autoclave
            </Button>
          </Box>
        </StyledDialogContent>
        <StyledDialogFooter>
          <Button variant="outlined" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={disableSave}>
            {isUpdating ? <CircularProgress size={20} /> : 'OK'}
          </Button>
        </StyledDialogFooter>
      </Dialog>
    </>
  )
}

export default AssignBoxModal
